import type { FlowFormData } from './types';

export type ExamplesFieldMismatch = {
  required: number;
  filled: number;
};

export type WhatsappExamplesMismatch = {
  body?: ExamplesFieldMismatch;
  header?: ExamplesFieldMismatch;
};

const pickString = (formData: FlowFormData, keys: string[]): string => {
  for (const key of keys) {
    const value = formData[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

/** Highest positional placeholder index in text, e.g. `{{1}}` + `{{3}}` → 3. */
export const countPositionalPlaceholders = (text: string): number => {
  let max = 0;
  for (const match of text.matchAll(/\{\{(\d+)\}\}/g)) {
    const index = Number(match[1]);
    if (Number.isFinite(index) && index > max) {
      max = index;
    }
  }
  return max;
};

/** How many of the first `required` example slots are non-empty trimmed strings. */
export const countFilledExamples = (examples: unknown, required: number): number => {
  if (required <= 0 || !Array.isArray(examples)) return 0;
  let filled = 0;
  for (let i = 0; i < required; i++) {
    const value = examples[i];
    if (typeof value === 'string' && value.trim()) {
      filled += 1;
    }
  }
  return filled;
};

export const hasEnoughExamples = (examples: unknown, required: number): boolean =>
  required <= 0 || countFilledExamples(examples, required) >= required;

/**
 * Returns body/header mismatches when positional variables lack matching non-empty examples.
 * Field names follow Meta (`content` / `header`) then legacy Comuni (`template_*`).
 */
export const getWhatsappExamplesMismatch = (formData: FlowFormData | undefined): WhatsappExamplesMismatch | null => {
  if (!formData) return null;

  const bodyText = pickString(formData, ['content', 'template_body_text']);
  const headerText = pickString(formData, ['header', 'template_header_text']);
  const bodyRequired = countPositionalPlaceholders(bodyText);
  const headerRequired = countPositionalPlaceholders(headerText);

  const result: WhatsappExamplesMismatch = {};

  if (bodyRequired > 0) {
    const filled = countFilledExamples(formData['body_examples'], bodyRequired);
    if (filled < bodyRequired) {
      result.body = { required: bodyRequired, filled };
    }
  }

  if (headerRequired > 0) {
    const filled = countFilledExamples(formData['header_examples'], headerRequired);
    if (filled < headerRequired) {
      result.header = { required: headerRequired, filled };
    }
  }

  return result.body || result.header ? result : null;
};
